import {
  Button,
  Card,
  Col,
  Form,
  message,
  Row,
  Select,
  Space,
  Tag,
  theme,
  Typography,
} from 'antd';
import React, { useEffect } from 'react';
import {
  AddSupervisorsToStudyLine,
  GetStudyLinesOfUniversityCoordinator,
  GetSupervisorsNotInStudyLine,
  InviteSupervisorsToStudyLine,
} from '../../helper/apiHelper';
import { useTranslation } from 'react-i18next';
import { validateMultiEmail } from '../../Validators/ValidatorEmail';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { StudyLinesAndSupervisorsCollapse } from './StudyLinesAndSupervisorsCollapse';

export const SupervisorManagementComponent = (): React.ReactElement => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const loggedInUser = useCurrentUser();
  const selectedStudyLineId = Form.useWatch('selectStudyLine', form);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { token } = theme.useToken();

  const { data: studyLines } = useQuery({
    queryKey: [
      ...QUERY_KEYS.STUDY_LINES_OF_UNIVERSITY_COORDINATOR,
      loggedInUser?.id,
    ],
    queryFn: async () => {
      if (!loggedInUser?.id) return null;
      return await GetStudyLinesOfUniversityCoordinator(
        loggedInUser.id.toString()
      );
    },
    enabled: !!loggedInUser?.id,
    meta: {
      onError: () => {
        messageApi.error(t('supvisManComp.fetchStudyLinesError'));
      },
    },
  });

  const { data: supervisors, isLoading: loadingSupervisors } = useQuery({
    queryKey: [...QUERY_KEYS.SUPERVISOR_NOT_IN_STUDY_LINE, selectedStudyLineId],
    queryFn: async () => {
      return await GetSupervisorsNotInStudyLine(selectedStudyLineId);
    },
    enabled: !!selectedStudyLineId,
    meta: {
      onError: () => {
        messageApi.error(t('supvisManComp.fetchSupVisError'));
      },
    },
  });

  const departmentNames = Array.from(
    new Set((studyLines ?? []).map((studyLine) => studyLine.departmentName))
  );

  // Fetch study lines on component mount
  useEffect(() => {
    if (studyLines && studyLines.length > 0) {
      form.setFieldValue('selectStudyLine', studyLines[0].id);
    }
  }, [studyLines, form]);

  // Fetch supervisors when study line changes
  useEffect(() => {
    if (!selectedStudyLineId) {
      form.resetFields(['selectInternalUsers']);
    }
  }, [form, selectedStudyLineId]);

  const clearFormFields = (fields?: string[]): void => {
    form.resetFields(fields);
  };

  const addSupervisorsToStudyLine = async (): Promise<void> => {
    try {
      if (!selectedStudyLineId) {
        messageApi.info(t('supvisManComp.selStudyLineFirst'));
        return;
      }

      if (!supervisors || supervisors.length === 0) {
        messageApi.warning(t('supvisManComp.noSupVis'));
        return;
      }

      const selectedSupervisorIds = form.getFieldValue('selectInternalUsers');
      if (!selectedSupervisorIds || selectedSupervisorIds.length === 0) {
        messageApi.info(t('supvisManComp.noSupVisSelected'));
        return;
      }

      await AddSupervisorsToStudyLine(
        selectedStudyLineId,
        selectedSupervisorIds
      );
      clearFormFields(['selectInternalUsers']);
      messageApi.success(t('supvisManComp.addSupVisSuccessStudyLine'));

      // Refresh supervisors list after adding
      queryClient.invalidateQueries({
        queryKey: [
          ...QUERY_KEYS.SUPERVISOR_NOT_IN_STUDY_LINE,
          selectedStudyLineId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.STUDY_LINES_WITH_SUPERVISORS,
      });
    } catch (error) {
      console.log(error);
      messageApi.error(t('supvisManComp.addSupVisErrorStudyLine'));
    }
  };

  const inviteSupervisorsToStudyLine = async (): Promise<void> => {
    try {
      await form.validateFields(['emails']);

      if (!selectedStudyLineId) {
        messageApi.info(t('supvisManComp.selStudyLineFirst'));
        return;
      }

      const supervisorEmails = form.getFieldValue('emails');
      if (!supervisorEmails || supervisorEmails.length === 0) {
        messageApi.info(t('supvisManComp.typeEmailToInvite'));
        return;
      }

      await InviteSupervisorsToStudyLine(selectedStudyLineId, supervisorEmails);
      clearFormFields(['emails']);
      messageApi.success(t('supvisManComp.inviteSuccessStudyLine'));

      // invalidate user to query refresh global user data
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.USER,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.STUDY_LINES_WITH_SUPERVISORS,
      });
    } catch (error) {
      console.log(error);
      messageApi.error(t('supvisManComp.inviteErrorStudyLine'));
    }
  };

  const filterOption = (
    input: string,
    option?: { label: string; value: number }
  ): boolean => {
    return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  };

  return (
    <>
      {contextHolder}
      {departmentNames.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong>
            {t('supvisManComp.yourDeps')}:{' '}
          </Typography.Text>
          {departmentNames.map((name) => (
            <Tag key={name}>{name}</Tag>
          ))}
        </div>
      )}
      <Space direction="vertical" size={50} style={{ width: '100%' }}>
        <Form form={form} size="middle">
          <Space direction="vertical" size="large" style={{ display: 'flex' }}>
            <Card style={{ boxShadow: token.boxShadow }}>
              <Row>
                <Col span={24}>
                  <Form.Item name="selectStudyLine">
                    <Select
                      style={{ minWidth: '100%' }}
                      showSearch
                      allowClear
                      placeholder={t('supvisManComp.selStudyLine')}
                      filterOption={filterOption}
                      options={studyLines?.map((studyLine) => ({
                        label: studyLine.name,
                        value: studyLine.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={24}>
                  <Form.Item name="selectInternalUsers">
                    <Select
                      style={{ minWidth: '100%' }}
                      showSearch
                      allowClear
                      mode="multiple"
                      placeholder={t('supvisManComp.selSupVisAdd')}
                      filterOption={filterOption}
                      loading={loadingSupervisors}
                      disabled={!selectedStudyLineId}
                      options={supervisors?.map((supvis) => ({
                        label:
                          `${supvis.lastName} ${supvis.firstName} (${supvis.email})`.trim(),
                        value: supvis.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={24}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="primary"
                      style={{ width: '100%' }}
                      onClick={addSupervisorsToStudyLine}
                      disabled={!selectedStudyLineId}
                    >
                      {t('supvisManComp.addToStudyLine')}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card style={{ boxShadow: token.boxShadow }}>
              <Row>
                <Col span={24}>
                  <Form.Item
                    name="emails"
                    rules={[{ validator: validateMultiEmail }]}
                  >
                    <Select
                      mode="tags"
                      style={{ minWidth: '100%' }}
                      allowClear
                      placeholder={t('supvisManComp.typeEmailToInvite')}
                      tokenSeparators={[',', ' ']}
                      disabled={!selectedStudyLineId}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={24}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="primary"
                      style={{ width: '100%' }}
                      onClick={inviteSupervisorsToStudyLine}
                      disabled={!selectedStudyLineId}
                    >
                      {t('supvisManComp.invToStudyLine')}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Space>
        </Form>

        <StudyLinesAndSupervisorsCollapse />
      </Space>
    </>
  );
};
